import type { ColumnDataType } from '@blocksuite/affine-model';
import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import {
  Container,
  createScope,
  type GeneralServiceIdentifier,
  type ServiceProvider,
} from '@blocksuite/global/di';
import { computed, effect, type ReadonlySignal } from '@preact/signals-core';

import type { TypeInstance } from '../logical/type.js';
import type { PropertyMetaConfig } from '../property/property-config.js';
import type { DatabaseFlags } from '../types.js';
import type { ViewConvertConfig } from '../view/convert.js';
import type { DataViewDataType, ViewMeta } from '../view/data-view.js';
import type { ViewManager } from '../view-manager/view-manager.js';
import {
  type DatabaseProjectionQuery,
  type DatabaseProjectionResult,
  type DatabaseProjectionSource,
  projectDatabaseRows,
} from './projection.js';
import {
  assertDataSourceBulkJob,
  clampDataSourceWindowLimit,
  DATA_SOURCE_BULK_COLUMN_MAX_LIMIT,
  DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
  normalizeDataSourcePropertyWindow,
  normalizeDataSourceWindow,
} from './window.js';

export type DataSourceWindowRequest = {
  offset?: number;
  limit: number;
};

export type DataSourceRowsWindow = {
  offset: number;
  limit: number;
  totalCount: number;
  hasMore: boolean;
  rowIds: string[];
};

export type DataSourcePropertiesWindow = {
  offset: number;
  limit: number;
  totalCount: number;
  propertyIds: string[];
};

export type DataSourceCellsWindowRequest = DataSourceWindowRequest & {
  propertyOffset?: number;
  propertyLimit?: number;
  propertyIds?: readonly string[];
};

export type DataSourceCellsWindow = {
  offset: number;
  limit: number;
  propertyOffset: number;
  propertyLimit: number;
  totalRowCount: number;
  totalColumnCount: number;
  rowIds: string[];
  propertyIds: string[];
  cells: Record<string, Record<string, unknown>>;
};

export type DataSourceBulkRowsRequest = DataSourceCellsWindowRequest & {
  jobId: string;
  purpose: string;
};

export type DataSourceWindowSubscriber = (
  window: DataSourceCellsWindow
) => void;

export type DataSourceViewWindowState = {
  propertyLimit?: number;
  propertyOffset?: number;
  rowLimit?: number;
  rowOffset?: number;
};

export type DataSourceWriteActor = 'user' | 'automation' | 'system';

export type DataSourceTableViewColumnLayout = {
  columnOrder?: readonly string[];
  columnWidths?: Readonly<Record<string, number>>;
};

export type DataSourceTableViewLayoutState = {
  columnLayoutSignature: string;
  intrinsicContentHeight?: number;
  measuredAt: number;
  rowHeights: Readonly<Record<string, number>>;
  version: 1;
};

export type DataSourceViewLayoutState = {
  intrinsicContentHeight: number;
  measuredAt: number;
  viewMode: string;
  version: 1;
};

export interface DataSource {
  readonly$: ReadonlySignal<boolean>;
  /** sl-9qm.21.3: true when database is system-managed (auto-populated, no manual edits) */
  systemManaged$: ReadonlySignal<boolean>;
  /** sl-9qm.21.3: true when system-managed DB allows row deletion (moderation) */
  allowDelete$: ReadonlySignal<boolean>;
  featureFlags$: ReadonlySignal<DatabaseFlags>;

  cellValueGet(rowId: string, propertyId: string): unknown;
  cellValueGet$(
    rowId: string,
    propertyId: string
  ): ReadonlySignal<unknown | undefined>;
  cellValueChange(
    rowId: string,
    propertyId: string,
    value: unknown,
    actor?: DataSourceWriteActor
  ): void;

  rowCount$: ReadonlySignal<number>;
  columnCount$: ReadonlySignal<number>;
  rowExists(rowId: string): boolean;
  getLogicalRowIdsSnapshot(): string[];
  getSearchCandidateRowIds(
    rowIds: readonly string[],
    searchText: string,
    propertyIds: readonly string[]
  ): readonly string[];
  getProjectionSource(query: DatabaseProjectionQuery): DatabaseProjectionSource;
  projectRows(query: DatabaseProjectionQuery): DatabaseProjectionResult;
  getRowsWindow(request: DataSourceWindowRequest): DataSourceRowsWindow;
  getBoundedRowsWindow(): DataSourceRowsWindow;
  getPropertyIdsWindow(
    request: DataSourceWindowRequest
  ): DataSourcePropertiesWindow;
  getAllPropertyIds(): string[];
  getBoundedPropertyIdsWindow(): DataSourcePropertiesWindow;
  getCellsWindow(request: DataSourceCellsWindowRequest): DataSourceCellsWindow;
  subscribeWindow(
    request: DataSourceCellsWindowRequest,
    subscriber: DataSourceWindowSubscriber
  ): () => void;
  bulkExportRows(request: DataSourceBulkRowsRequest): DataSourceCellsWindow;
  bulkAutomationRows(request: DataSourceBulkRowsRequest): DataSourceCellsWindow;
  rowAdd(InsertToPosition: InsertToPosition | number): string;
  rowCanAdd(): boolean;
  rowAddDisabledReason(): string | undefined;
  rowDelete(ids: string[], actor?: DataSourceWriteActor): void;
  rowMove(rowId: string, position: InsertToPosition): void;

  propertyMetas$: ReadonlySignal<PropertyMetaConfig[]>;
  allPropertyMetas$: ReadonlySignal<PropertyMetaConfig[]>;

  propertyNameGet$(propertyId: string): ReadonlySignal<string | undefined>;
  propertyNameGet(propertyId: string): string;
  propertyNameSet(propertyId: string, name: string): void;

  propertyTypeGet(propertyId: string): string | undefined;
  propertyTypeGet$(propertyId: string): ReadonlySignal<string | undefined>;
  propertyTypeSet(propertyId: string, type: string): void;
  propertyTypeCanSet(propertyId: string): boolean;

  propertyDataGet(propertyId: string): Record<string, unknown>;
  propertyDataGet$(
    propertyId: string
  ): ReadonlySignal<Record<string, unknown> | undefined>;
  propertyDataSet(propertyId: string, data: Record<string, unknown>): void;

  propertyDataTypeGet(propertyId: string): TypeInstance | undefined;
  propertyDataTypeGet$(
    propertyId: string
  ): ReadonlySignal<TypeInstance | undefined>;

  propertyReadonlyGet(propertyId: string): boolean;
  propertyReadonlyGet$(propertyId: string): ReadonlySignal<boolean>;

  /** Whether values in this property are immutable for the current user. */
  propertyValueReadonlyGet(propertyId: string): boolean;
  propertyValueReadonlyGet$(propertyId: string): ReadonlySignal<boolean>;

  propertyMetaGet(type: string): PropertyMetaConfig | undefined;
  propertyAdd(
    insertToPosition: InsertToPosition,
    ops?: {
      type?: string;
      name?: string;
    }
  ): string | undefined;

  propertyDuplicate(propertyId: string): string | undefined;
  propertyCanDuplicate(propertyId: string): boolean;

  propertyDelete(id: string): void;
  propertyCanDelete(propertyId: string): boolean;

  provider: ServiceProvider;
  serviceGet<T>(key: GeneralServiceIdentifier<T>): T | null;
  serviceGetOrCreate<T>(key: GeneralServiceIdentifier<T>, create: () => T): T;

  viewConverts: ViewConvertConfig[];
  viewManager: ViewManager;
  viewMetas: ViewMeta[];
  viewDataList$: ReadonlySignal<DataViewDataType[]>;

  viewDataGet(viewId: string): DataViewDataType | undefined;
  viewDataGet$(viewId: string): ReadonlySignal<DataViewDataType | undefined>;

  viewDataAdd(viewData: DataViewDataType): string;
  viewDataDuplicate(id: string): string;
  viewDataDelete(viewId: string): void;
  viewDataMoveTo(id: string, position: InsertToPosition): void;
  viewDataUpdate<ViewData extends DataViewDataType>(
    id: string,
    updater: (data: ViewData) => Partial<ViewData>
  ): void;

  getViewWindowState(viewId: string): DataSourceViewWindowState | undefined;
  setViewWindowState(viewId: string, patch: DataSourceViewWindowState): boolean;
  getViewLayoutState(
    viewId: string,
    viewMode: string
  ): DataSourceViewLayoutState | undefined;
  setViewLayoutState(viewId: string, state: DataSourceViewLayoutState): boolean;
  getTableViewLayoutState(
    viewId: string,
    columnLayoutSignature: string
  ): DataSourceTableViewLayoutState | undefined;
  setTableViewLayoutState(
    viewId: string,
    state: DataSourceTableViewLayoutState
  ): boolean;
  getTableViewColumnOrder(viewId: string): readonly string[] | undefined;
  setTableViewColumnOrder(
    viewId: string,
    columnOrder: readonly string[]
  ): boolean;
  getTableViewColumnWidth(
    viewId: string,
    propertyId: string
  ): number | undefined;
  setTableViewColumnWidth(
    viewId: string,
    propertyId: string,
    width: number
  ): boolean;

  viewMetaGet(type: string): ViewMeta;
  viewMetaGet$(type: string): ReadonlySignal<ViewMeta | undefined>;

  viewMetaGetById(viewId: string): ViewMeta | undefined;
  viewMetaGetById$(viewId: string): ReadonlySignal<ViewMeta | undefined>;
}

export const DataSourceScope = createScope('data-source');

export abstract class DataSourceBase implements DataSource {
  getTableViewColumnOrder(_viewId: string): readonly string[] | undefined {
    return undefined;
  }

  setTableViewColumnOrder(
    _viewId: string,
    _columnOrder: readonly string[]
  ): boolean {
    return false;
  }

  getTableViewColumnWidth(
    _viewId: string,
    _propertyId: string
  ): number | undefined {
    return undefined;
  }

  setTableViewColumnWidth(
    _viewId: string,
    _propertyId: string,
    _width: number
  ): boolean {
    return false;
  }

  propertyTypeCanSet(propertyId: string): boolean {
    return !this.isFixedProperty(propertyId);
  }
  propertyCanDuplicate(propertyId: string): boolean {
    return !this.isFixedProperty(propertyId);
  }
  propertyCanDelete(propertyId: string): boolean {
    return !this.isFixedProperty(propertyId);
  }
  protected container = new Container();

  abstract get parentProvider(): ServiceProvider;

  abstract featureFlags$: ReadonlySignal<DatabaseFlags>;

  protected abstract sourcePropertyIds$: ReadonlySignal<string[]>;

  abstract propertyMetas$: ReadonlySignal<PropertyMetaConfig[]>;

  abstract allPropertyMetas$: ReadonlySignal<PropertyMetaConfig[]>;

  abstract readonly$: ReadonlySignal<boolean>;

  /** sl-9qm.21.3: Override in subclass if database supports system-managed mode */
  systemManaged$: ReadonlySignal<boolean> = computed(() => false);

  /** sl-9qm.21.3: Override in subclass if system-managed DB allows row deletion */
  allowDelete$: ReadonlySignal<boolean> = computed(() => false);

  rowCanAdd(): boolean {
    return true;
  }

  rowAddDisabledReason(): string | undefined {
    return undefined;
  }

  protected abstract sourceRowIds$: ReadonlySignal<string[]>;

  rowCount$: ReadonlySignal<number> = computed(
    () => this.sourceRowIds$.value.length
  );

  columnCount$: ReadonlySignal<number> = computed(
    () => this.sourcePropertyIds$.value.length
  );

  rowExists(rowId: string): boolean {
    return this.sourceRowIds$.value.includes(rowId);
  }

  getLogicalRowIdsSnapshot(): string[] {
    return this.sourceRowIds$.value.slice();
  }

  getSearchCandidateRowIds(
    rowIds: readonly string[],
    _searchText: string,
    _propertyIds: readonly string[]
  ): readonly string[] {
    return rowIds;
  }

  getProjectionSource(
    _query: DatabaseProjectionQuery
  ): DatabaseProjectionSource {
    return {
      complete: true,
      diagnostics: [],
      freshness: 'live',
      rowIds: this.sourceRowIds$.value,
      source: 'local-shards',
      syncState: 'live',
    };
  }

  projectRows(query: DatabaseProjectionQuery): DatabaseProjectionResult {
    const source = this.getProjectionSource(query);
    const result = projectDatabaseRows({
      complete: source.complete,
      freshness: source.freshness,
      propertyIds: this.getAllPropertyIds(),
      query,
      readCellValue: (rowId, propertyId) =>
        this.cellValueGet(rowId, propertyId),
      rowIds: source.rowIds,
      source: source.source,
      syncState: source.syncState,
    });
    return source.diagnostics.length
      ? {
          ...result,
          diagnostics: [...source.diagnostics, ...result.diagnostics],
        }
      : result;
  }

  abstract viewConverts: ViewConvertConfig[];

  abstract viewDataList$: ReadonlySignal<DataViewDataType[]>;

  abstract viewManager: ViewManager;

  abstract viewMetas: ViewMeta[];

  getViewWindowState(_viewId: string): DataSourceViewWindowState | undefined {
    return undefined;
  }

  setViewWindowState(
    _viewId: string,
    _patch: DataSourceViewWindowState
  ): boolean {
    return false;
  }

  getViewLayoutState(
    _viewId: string,
    _viewMode: string
  ): DataSourceViewLayoutState | undefined {
    return undefined;
  }

  setViewLayoutState(
    _viewId: string,
    _state: DataSourceViewLayoutState
  ): boolean {
    return false;
  }

  getTableViewLayoutState(
    _viewId: string,
    _columnLayoutSignature: string
  ): DataSourceTableViewLayoutState | undefined {
    return undefined;
  }

  setTableViewLayoutState(
    _viewId: string,
    _state: DataSourceTableViewLayoutState
  ): boolean {
    return false;
  }

  getRowsWindow(request: DataSourceWindowRequest): DataSourceRowsWindow {
    const { offset, limit } = normalizeDataSourceWindow(request);
    const rows = this.sourceRowIds$.value;
    return {
      offset,
      limit,
      totalCount: rows.length,
      hasMore: offset + limit < rows.length,
      rowIds: rows.slice(offset, offset + limit),
    };
  }

  getBoundedRowsWindow(): DataSourceRowsWindow {
    return this.getRowsWindow({
      offset: 0,
      limit: clampDataSourceWindowLimit(this.rowCount$.value),
    });
  }

  getPropertyIdsWindow(
    request: DataSourceWindowRequest
  ): DataSourcePropertiesWindow {
    const { offset, limit } = normalizeDataSourceWindow(
      request,
      DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
    );
    const properties = this.sourcePropertyIds$.value;
    return {
      offset,
      limit,
      totalCount: properties.length,
      propertyIds: properties.slice(offset, offset + limit),
    };
  }

  getAllPropertyIds(): string[] {
    return this.sourcePropertyIds$.value.slice();
  }

  getBoundedPropertyIdsWindow(): DataSourcePropertiesWindow {
    return this.getPropertyIdsWindow({
      offset: 0,
      limit: clampDataSourceWindowLimit(
        this.columnCount$.value,
        DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
      ),
    });
  }

  getCellsWindow(request: DataSourceCellsWindowRequest): DataSourceCellsWindow {
    return this.getCellsWindowWithPropertyLimit(
      request,
      DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
      request.propertyIds ?? this.getBoundedPropertyIdsWindow().propertyIds
    );
  }

  private getCellsWindowWithPropertyLimit(
    request: DataSourceCellsWindowRequest,
    maxPropertyLimit: number,
    propertySource: readonly string[]
  ): DataSourceCellsWindow {
    const rowsWindow = this.getRowsWindow(request);
    const { propertyOffset, propertyLimit, propertyIds } =
      normalizeDataSourcePropertyWindow(
        request,
        propertySource,
        maxPropertyLimit
      );
    const cells: Record<string, Record<string, unknown>> = {};

    for (const rowId of rowsWindow.rowIds) {
      const rowCells: Record<string, unknown> = {};
      for (const propertyId of propertyIds) {
        rowCells[propertyId] = this.cellValueGet(rowId, propertyId);
      }
      cells[rowId] = rowCells;
    }

    return {
      offset: rowsWindow.offset,
      limit: rowsWindow.limit,
      propertyOffset,
      propertyLimit,
      totalRowCount: rowsWindow.totalCount,
      totalColumnCount: this.columnCount$.value,
      rowIds: rowsWindow.rowIds,
      propertyIds,
      cells,
    };
  }

  subscribeWindow(
    request: DataSourceCellsWindowRequest,
    subscriber: DataSourceWindowSubscriber
  ): () => void {
    return effect(() => {
      subscriber(this.getCellsWindow(request));
    });
  }

  bulkExportRows(request: DataSourceBulkRowsRequest): DataSourceCellsWindow {
    return this.getBulkRowsWindow(request);
  }

  bulkAutomationRows(
    request: DataSourceBulkRowsRequest
  ): DataSourceCellsWindow {
    return this.getBulkRowsWindow(request);
  }

  private getBulkRowsWindow(
    request: DataSourceBulkRowsRequest
  ): DataSourceCellsWindow {
    assertDataSourceBulkJob(request);
    return this.getCellsWindowWithPropertyLimit(
      request,
      DATA_SOURCE_BULK_COLUMN_MAX_LIMIT,
      request.propertyIds ?? this.sourcePropertyIds$.value
    );
  }

  abstract cellValueChange(
    rowId: string,
    propertyId: string,
    value: unknown
  ): void;

  abstract cellValueGet(rowId: string, propertyId: string): unknown;

  cellValueGet$(
    rowId: string,
    propertyId: string
  ): ReadonlySignal<unknown | undefined> {
    return computed(() => this.cellValueGet(rowId, propertyId));
  }

  get provider() {
    return this.container.provider(DataSourceScope, this.parentProvider);
  }

  serviceGet<T>(key: GeneralServiceIdentifier<T>): T | null {
    return this.provider.getOptional(key);
  }

  serviceSet<T>(key: GeneralServiceIdentifier<T>, value: T): void {
    this.container.addValue(key, value, { scope: DataSourceScope });
  }

  serviceGetOrCreate<T>(key: GeneralServiceIdentifier<T>, create: () => T): T {
    const result = this.serviceGet(key);
    if (result != null) {
      return result;
    }
    const value = create();
    this.serviceSet(key, value);
    return value;
  }

  abstract propertyAdd(
    insertToPosition: InsertToPosition,
    ops?: {
      type?: string;
      name?: string;
    }
  ): string | undefined;

  abstract propertyDataGet(propertyId: string): Record<string, unknown>;

  propertyDataGet$(
    propertyId: string
  ): ReadonlySignal<Record<string, unknown> | undefined> {
    return computed(() => this.propertyDataGet(propertyId));
  }

  abstract propertyDataSet(
    propertyId: string,
    data: Record<string, unknown>
  ): void;

  abstract propertyDataTypeGet(propertyId: string): TypeInstance | undefined;

  propertyDataTypeGet$(
    propertyId: string
  ): ReadonlySignal<TypeInstance | undefined> {
    return computed(() => this.propertyDataTypeGet(propertyId));
  }

  abstract propertyDelete(id: string): void;

  abstract propertyDuplicate(propertyId: string): string | undefined;

  abstract propertyMetaGet(type: string): PropertyMetaConfig | undefined;

  abstract propertyNameGet(propertyId: string): string;

  propertyNameGet$(propertyId: string): ReadonlySignal<string | undefined> {
    return computed(() => this.propertyNameGet(propertyId));
  }

  abstract propertyNameSet(propertyId: string, name: string): void;

  propertyReadonlyGet(_propertyId: string): boolean {
    return false;
  }

  propertyReadonlyGet$(propertyId: string): ReadonlySignal<boolean> {
    return computed(() => this.propertyReadonlyGet(propertyId));
  }

  propertyValueReadonlyGet(propertyId: string): boolean {
    return this.propertyReadonlyGet(propertyId);
  }

  propertyValueReadonlyGet$(propertyId: string): ReadonlySignal<boolean> {
    return computed(() => this.propertyValueReadonlyGet(propertyId));
  }

  abstract propertyTypeGet(propertyId: string): string | undefined;

  propertyTypeGet$(propertyId: string): ReadonlySignal<string | undefined> {
    return computed(() => this.propertyTypeGet(propertyId));
  }

  abstract propertyTypeSet(propertyId: string, type: string): void;

  abstract rowAdd(InsertToPosition: InsertToPosition | number): string;

  abstract rowDelete(ids: string[], actor?: DataSourceWriteActor): void;

  abstract rowMove(rowId: string, position: InsertToPosition): void;

  abstract viewDataAdd(viewData: DataViewDataType): string;

  abstract viewDataDelete(viewId: string): void;

  abstract viewDataDuplicate(id: string): string;

  abstract viewDataGet(viewId: string): DataViewDataType | undefined;

  viewDataGet$(viewId: string): ReadonlySignal<DataViewDataType | undefined> {
    return computed(() => this.viewDataGet(viewId));
  }

  abstract viewDataMoveTo(id: string, position: InsertToPosition): void;

  abstract viewDataUpdate<ViewData extends DataViewDataType>(
    id: string,
    updater: (data: ViewData) => Partial<ViewData>
  ): void;

  abstract viewMetaGet(type: string): ViewMeta;

  viewMetaGet$(type: string): ReadonlySignal<ViewMeta | undefined> {
    return computed(() => this.viewMetaGet(type));
  }

  abstract viewMetaGetById(viewId: string): ViewMeta | undefined;

  viewMetaGetById$(viewId: string): ReadonlySignal<ViewMeta | undefined> {
    return computed(() => this.viewMetaGetById(viewId));
  }

  fixedProperties$ = computed(() => {
    return this.allPropertyMetas$.value
      .filter(v => v.config.fixed)
      .map(v => v.type);
  });
  fixedPropertySet = computed(() => {
    return new Set(this.fixedProperties$.value);
  });

  protected abstract getNormalPropertyAndIndex(propertyId: string):
    | {
        column: ColumnDataType<Record<string, unknown>>;
        index: number;
      }
    | undefined;

  isFixedProperty(propertyId: string) {
    if (this.fixedPropertySet.value.has(propertyId)) {
      return true;
    }
    const result = this.getNormalPropertyAndIndex(propertyId);
    if (result) {
      return this.fixedPropertySet.value.has(result.column.type);
    }
    return false;
  }
}
